import React, { useState, useEffect } from "react";
import { generateTaxFormPDF } from "./pdfGenerator";
import { getQuestions } from "./questionDataService";
import companyLogo from "../../Assets/BooksCompl.png";
import "./Tax.css";

const QuestionsForm = ({
  onClose, onSubmit, initialAnswers, initialComments, initialFiles = [], isSubmitted, formNumber, memberType = "primary", memberIndex = 0, memberName = ""
}) => {
  const MAX_FILES = 20;
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [comments, setComments] = useState(initialComments || {});
  const [expanded, setExpanded] = useState({});
  const [files, setFiles] = useState(() => {
    console.log('QuestionsForm initialFiles:', initialFiles);
    // Convert stored file data to the format expected by the component
    if (initialFiles && initialFiles.length > 0) {
      const convertedFiles = initialFiles.map(file => {
        // If it's already in the correct format (has file property), keep it
        if (file.file) {
          return file;
        }
        // If it's stored file data (has url property), convert it
        if (file.url) {
          return {
            name: file.name,
            size: file.size,
            type: file.type,
            url: file.url,
            isStored: true // Flag to indicate this is a stored file
          };
        }
        return file;
      });
      console.log('QuestionsForm converted files:', convertedFiles);
      return convertedFiles;
    }
    return [];
  });
  const [uploadProgress] = useState({});
  const [formQuestions, setFormQuestions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const questions = await getQuestions(formNumber);
        setFormQuestions(questions);
        setError('');
      } catch (err) {
        setError('Failed to load questions. Please try again.');
        console.error('Error fetching questions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [formNumber]);

  // Debug effect to monitor initialFiles changes
  useEffect(() => {
    console.log('QuestionsForm initialFiles changed:', initialFiles);
    if (initialFiles && initialFiles.length > 0) {
      const convertedFiles = initialFiles.map(file => {
        console.log('Processing file:', file);
        // If it's already in the correct format (has file property), keep it
        if (file.file) {
          return file;
        }
        // If it's stored file data (has url property), convert it
        if (file.url) {
          return {
            name: file.name,
            size: file.size,
            type: file.type,
            url: file.url,
            isStored: true // Flag to indicate this is a stored file
          };
        }
        return file;
      });
      console.log('Setting files to:', convertedFiles);
      setFiles(convertedFiles);
    } else {
      console.log('No initialFiles, setting files to empty array');
      setFiles([]);
    }
  }, [initialFiles]);

  const totalQuestions = Object.values(formQuestions).reduce(
    (total, section) => total + section.length,
    0
  );

  const handleAnswer = (index, value) => {
    if (!isSubmitted) {
      setAnswers((prev) => ({ ...prev, [index]: value }));
    }
  };

  const handleComment = (index, value) => {
    if (!isSubmitted) {
      setComments((prev) => ({ ...prev, [index]: value }));
    }
  };

  const toggleQuestion = (question) => {
    if (!isSubmitted) {
      setExpanded((prev) => ({ ...prev, [question]: !prev[question] }));
    }
  };

  const calculateProgress = () => {
    return Object.keys(answers).filter((k) => answers[k] !== undefined).length;
  };

  const calculateSectionProgress = (section) => {
    return formQuestions[section].filter((_, index) => answers[`${section}-${index}`] !== undefined).length;
  };

  const handleFileChange = (e) => {
    if (!isSubmitted && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const currentFileCount = files.length;
      const availableSlots = MAX_FILES - currentFileCount;

      if (newFiles.length > availableSlots) {
        alert(`You can only upload up to ${MAX_FILES} files. You currently have ${currentFileCount} files and tried to add ${newFiles.length} more files.`);
        return;
      }

      const formattedNewFiles = newFiles.map(file => ({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 0
      }));

      setFiles(prev => [...prev, ...formattedNewFiles]);
      e.target.value = '';
    }
  };

  const removeFile = (index) => {
    if (!isSubmitted) {
      setFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSave = () => {
    if (!isSubmitted) {
      const currentProgress = calculateProgress();
      
      // Convert files to the format expected by the backend
      const formattedFiles = files.map(file => {
        if (file.isStored) {
          return {
            name: file.name,
            size: file.size,
            type: file.type,
            url: file.url
          };
        }
        return {
          name: file.name,
          size: file.size,
          type: file.type,
          url: file.url || ''
        };
      });
      
      onSubmit({ answers, comments, files: formattedFiles }, false, memberType, memberIndex);
      alert(`Saved progress: ${currentProgress}/${totalQuestions} questions answered`);
    }
  };

  const handleConfirm = async () => {
    const currentProgress = calculateProgress();
    if (currentProgress < 1) {
      alert("Please answer all questions.");
      return;
    }

    if (!isSubmitted) {
      try {
        let fileUrls = [];
        if (files.length > 0) {
          // Separate new files from stored files
          const newFiles = files.filter(file => file.file && !file.isStored);
          const storedFiles = files.filter(file => file.isStored);
          
          // Upload new files if any
          if (newFiles.length > 0) {
            const formData = new FormData();
            newFiles.forEach(file => {
              formData.append('files', file.file);
            });

            const token = localStorage.getItem("token");
            const uploadResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/upload-files`, {
              method: "POST",
              body: formData,
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (!uploadResponse.ok) {
              throw new Error("Failed to upload files");
            }

            const uploadResult = await uploadResponse.json();
            fileUrls = uploadResult.fileUrls || [];
          }
          
          // Add stored file URLs
          storedFiles.forEach(file => {
            fileUrls.push(file.url);
          });
        }

        const pdfBlob = generateTaxFormPDF(
          formQuestions,
          answers,
          comments,
          formNumber,
          companyLogo,
          memberType,
          memberIndex
        );

        const pdfFormData = new FormData();
        let pdfName = "Info.Checklist filled.pdf";
        if (memberType !== "primary") {
          pdfName = `Info.Checklist filled (${memberName || `Member ${memberIndex + 1}`}).pdf`;
        }
        pdfFormData.append('pdf', pdfBlob, pdfName);

        const pdfToken = localStorage.getItem("token");
        const pdfResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/upload-pdf`, {
          method: "POST",
          body: pdfFormData,
          headers: {
            Authorization: `Bearer ${pdfToken}`,
          },
        });

        if (!pdfResponse.ok) {
          throw new Error("Failed to upload PDF");
        }

        const pdfResult = await pdfResponse.json();

        const submittedFiles = files.map(file => {
          // If it's a stored file, use its existing URL
          if (file.isStored && file.url) {
            return {
              name: file.name,
              size: file.size,
              type: file.type,
              url: file.url
            };
          }
          
          // For new files, find the matching URL from upload response
          const matchingUrl = Array.isArray(fileUrls)
            ? fileUrls.find(url => typeof url === 'string' && url.includes(encodeURIComponent(file.name)))
            : '';

          return {
            name: file.name,
            size: file.size,
            type: file.type,
            url: matchingUrl || ''
          };
        });

        onSubmit({
          answers,
          comments,
          pdfUrl: pdfResult.pdfUrl,
          fileUrls,
          files: submittedFiles
        }, true, memberType, memberIndex);

        alert("Form submitted successfully!");
      } catch (error) {
        console.error("Error in form submission:", error);
        alert(`Failed to submit form: ${error.message}`);
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="tax-filing-modal">
        <div className="tax-filing-modal-content">
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading questions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tax-filing-modal">
        <div className="tax-filing-modal-content">
          <div className="text-center p-5">
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
            <button className="btn btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tax-filing-modal">
      <div className="tax-filing-modal-content">

        <div className="tax-filing-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
            <h2 className="tax-filing-title" style={{ fontSize: 'clamp(1.1rem, 4vw, 1.5rem)' }}>
              Initial Information (Checklist)
            </h2>
            <div className="tax-filing-progress" style={{ 
              fontSize: '0.9em', 
              color: '#4a5568',
              backgroundColor: '#f7fafc',
              padding: '0.4rem 0.8rem',
              borderRadius: '9999px',
              border: '1px solid #e2e8f0'
            }}>
              {calculateProgress()}/{totalQuestions}
            </div>
          </div>
          <div className="tax-filing-instructions" style={{ margin: '0 0 0.5rem 0', lineHeight: '1.5' }}>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <span style={{ minWidth: '1.5rem', fontWeight: 500 }}>1.</span>
              <span>Please upload documents in Part A.</span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <span style={{ minWidth: '1.5rem', fontWeight: 500 }}>2.</span>
              <span>Parts B, C etc. will be completed by our team for your review and confirmation.</span>
            </div>
          </div>
        </div>

        <div className="tax-filing-questions-container">
          {Object.keys(formQuestions).map((questionTitle, index) => {
            const sectionLetter = String.fromCharCode(65 + index);
            const sectionProgress = calculateSectionProgress(questionTitle);
            return (  
              <div key={index} className="tax-filing-question" onClick={() => toggleQuestion(questionTitle)}>
                <div className="tax-filing-question-header">
                  <span className="tax-filing-question-number">{sectionLetter}.</span>
                  <span className="tax-filing-question-title">{questionTitle}</span>
                  <span className="tax-filing-section-progress">
                    {sectionProgress}/{formQuestions[questionTitle].length}
                  </span>
                </div>
                {expanded[questionTitle] && (
                  <div className="tax-filing-sub-questions">
                    {formQuestions[questionTitle].map((subQuestion, subIndex) => (
                      <div key={subIndex} className="tax-filing-sub-question">
                        <p className="tax-filing-sub-question-text">
                          <span className="tax-filing-sub-question-number">{subIndex + 1}.</span> {subQuestion}
                        </p>
                        <div className="tax-filing-options">
                          {["yes", "no", "na", "comment"].map((option) => (
                            <button
                              key={option}
                              className={`tax-filing-option ${answers[`${questionTitle}-${subIndex}`] === option ? `tax-filing-selected-${option}` : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAnswer(`${questionTitle}-${subIndex}`, option);
                              }}
                              disabled={isSubmitted}
                              aria-label={`Select ${option}`}
                            >
                              {option.toUpperCase()}
                            </button>
                          ))}
                        </div>
                        {answers[`${questionTitle}-${subIndex}`] === "comment" && (
                          <textarea
                            placeholder="Add a comment..."
                            value={comments[`${questionTitle}-${subIndex}`] || ""}
                            onChange={(e) => handleComment(`${questionTitle}-${subIndex}`, e.target.value)}
                            className="tax-filing-comment-box"
                            disabled={isSubmitted}
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Comment input"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="tax-filing-file-section">
          <div className="tax-filing-file-upload">
            <label className="tax-filing-file-upload-label">
              <input
                type="file"
                onChange={handleFileChange}
                disabled={isSubmitted}
                multiple
                className="tax-filing-file-input"
                aria-label="Upload files"
              />
              <span className="tax-filing-file-upload-button">Choose Files</span>
              <span className="tax-filing-file-upload-text">
                {files.length > 0 ? `${files.length}/${MAX_FILES} files selected` : "No files chosen"}
              </span>
            </label>
          </div>

          {files.length > 0 && (
            <div className="tax-filing-file-list-container">
              <div className="tax-filing-file-list">
                {files.map((file, index) => (
                  <div key={index} className="tax-filing-file-item">
                    <div className="tax-filing-file-info">
                      <span className="tax-filing-file-name" title={file.name}>
                        {file.name.length > 30 ? `${file.name.substring(0, 30)}...` : file.name}
                      </span>
                      <span className="tax-filing-file-size">{formatFileSize(file.size)}</span>
                    </div>
                    {!isSubmitted && (
                      <button
                        onClick={() => removeFile(index)}
                        className="tax-filing-remove-file"
                        aria-label={`Remove file ${file.name}`}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    )}
                    {uploadProgress[index] > 0 && uploadProgress[index] < 100 && (
                      <div className="tax-filing-upload-progress">
                        <div
                          className="tax-filing-upload-progress-bar"
                          style={{ width: `${uploadProgress[index]}%` }}
                        ></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="tax-filing-form-actions">
          <button className="tax-filing-btn tax-filing-close" onClick={onClose}>
            Close
          </button>
          <div className="tax-filing-right-buttons">
            <button
              className="tax-filing-btn tax-filing-save"
              onClick={handleSave}
              disabled={isSubmitted}
            >
              Save
            </button>
            <button
              className="tax-filing-btn tax-filing-confirm"
              onClick={handleConfirm}
              disabled={isSubmitted}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionsForm;